"use client";

import * as React from "react";
import { useTranslation } from "react-i18next";
import { HardDrive, Loader2, Play, RefreshCw, Server } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { formatBytes } from "@/utils/format";
import { fetchWithCsrf } from "./fetchWithCsrf";
import { resolveApiUrl } from "./resolveApiUrl";

type HostMetrics = {
  available: boolean;
  memory: { totalBytes: number; availableBytes: number; usedBytes: number } | null;
  load: { load1: number; load5: number; load15: number } | null;
  disk: {
    mount: string;
    totalBytes: number;
    usedBytes: number;
    availableBytes: number;
    usedPercent: number;
  } | null;
  platform: string;
};

type PlatformBackup = {
  id: string;
  backupType: string;
  storagePath: string;
  sizeBytes: number | null;
  status: string;
  errorMessage: string | null;
  createdAt: string;
};

type AuditLog = {
  id: string;
  userId: string | null;
  action: string;
  resourceType: string;
  resourceId: string | null;
  metadata: unknown;
  ipAddress: string | null;
  createdAt: string;
};

const readError = async (response: Response, fallback: string): Promise<string> => {
  const body = (await response.json().catch(() => ({}))) as { error?: unknown };
  return typeof body.error === "string" ? body.error : fallback;
};

export const OperatorOpsPageClient = (): React.ReactElement => {
  const { t } = useTranslation();
  const [metrics, setMetrics] = React.useState<HostMetrics | null>(null);
  const [backupRoot, setBackupRoot] = React.useState("");
  const [backups, setBackups] = React.useState<PlatformBackup[]>([]);
  const [logs, setLogs] = React.useState<AuditLog[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [runningBackup, setRunningBackup] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const loadAll = React.useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [metricsRes, backupsRes, logsRes] = await Promise.all([
        fetch(resolveApiUrl("/api/admin/host-metrics"), { credentials: "include" }),
        fetch(resolveApiUrl("/api/admin/backups"), { credentials: "include" }),
        fetch(resolveApiUrl("/api/admin/audit-logs?limit=100"), { credentials: "include" })
      ]);

      if (!metricsRes.ok) throw new Error(await readError(metricsRes, t("operatorOps.metricsLoadFailed")));
      if (!backupsRes.ok) throw new Error(await readError(backupsRes, t("operatorOps.backupsLoadFailed")));
      if (!logsRes.ok) throw new Error(await readError(logsRes, t("operatorOps.auditLoadFailed")));

      setMetrics((await metricsRes.json()) as HostMetrics);
      const backupPayload = (await backupsRes.json()) as { backupRoot?: string; backups?: PlatformBackup[] };
      setBackupRoot(backupPayload.backupRoot ?? "");
      setBackups(backupPayload.backups ?? []);
      const logPayload = (await logsRes.json()) as { logs?: AuditLog[] };
      setLogs(logPayload.logs ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("operatorOps.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  React.useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const runBackup = async (): Promise<void> => {
    setRunningBackup(true);
    try {
      const response = await fetchWithCsrf("/api/admin/backups/run", { method: "POST" });
      if (!response.ok) throw new Error(await readError(response, t("operatorOps.backupRunFailed")));
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("operatorOps.backupRunFailed"));
    } finally {
      setRunningBackup(false);
    }
  };

  if (loading && !metrics) {
    return <p className="text-sm text-muted-foreground">{t("common.loading")}</p>;
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t("operatorOps.heading")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("operatorOps.intro")}</p>
        </div>
        <Button type="button" variant="outline" onClick={() => void loadAll()} disabled={loading}>
          <RefreshCw className={loading ? "size-4 animate-spin" : "size-4"} aria-hidden />
          {t("operatorOps.refresh")}
        </Button>
      </div>

      {error ? <p className="text-sm text-destructive">{error}</p> : null}

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Server className="size-4" aria-hidden />
              {t("operatorOps.hostMetrics")}
            </CardTitle>
            <CardDescription>{metrics?.available ? t("operatorOps.metricsAvailable") : t("operatorOps.metricsUnavailable")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {metrics?.memory ? (
              <p>
                {t("operatorOps.memory")}: {formatBytes(metrics.memory.usedBytes)} / {formatBytes(metrics.memory.totalBytes)}
              </p>
            ) : null}
            {metrics?.load ? (
              <p>
                {t("operatorOps.load")}: {metrics.load.load1.toFixed(2)} / {metrics.load.load5.toFixed(2)} / {metrics.load.load15.toFixed(2)}
              </p>
            ) : null}
            {metrics?.disk ? (
              <p>
                {t("operatorOps.disk")}: {metrics.disk.usedPercent}% ({formatBytes(metrics.disk.usedBytes)} / {formatBytes(metrics.disk.totalBytes)})
              </p>
            ) : null}
            <p className="text-xs text-muted-foreground">{metrics?.platform ?? "—"}</p>
          </CardContent>
        </Card>

        <Card className="md:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-3">
              <div>
                <CardTitle className="flex items-center gap-2 text-base">
                  <HardDrive className="size-4" aria-hidden />
                  {t("operatorOps.backups")}
                </CardTitle>
                <CardDescription>{backupRoot || t("operatorOps.backupsIntro")}</CardDescription>
              </div>
              <Button type="button" disabled={runningBackup} onClick={() => void runBackup()}>
                {runningBackup ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <Play className="size-4" aria-hidden />}
                {t("operatorOps.runBackup")}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {backups.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("operatorOps.noBackups")}</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("operatorOps.backupType")}</TableHead>
                    <TableHead>{t("operatorOps.backupStatus")}</TableHead>
                    <TableHead>{t("operatorOps.backupSize")}</TableHead>
                    <TableHead>{t("operatorOps.backupCreated")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {backups.map((backup) => (
                    <TableRow key={backup.id}>
                      <TableCell>{backup.backupType}</TableCell>
                      <TableCell>{backup.status}</TableCell>
                      <TableCell>{backup.sizeBytes != null ? formatBytes(backup.sizeBytes) : "—"}</TableCell>
                      <TableCell>{new Date(backup.createdAt).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("operatorOps.auditLog")}</CardTitle>
          <CardDescription>{t("operatorOps.auditIntro")}</CardDescription>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("operatorOps.noAuditLogs")}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("operatorOps.auditTime")}</TableHead>
                  <TableHead>{t("operatorOps.auditAction")}</TableHead>
                  <TableHead>{t("operatorOps.auditResource")}</TableHead>
                  <TableHead>{t("operatorOps.auditUser")}</TableHead>
                  <TableHead>{t("operatorOps.auditIp")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>{new Date(log.createdAt).toLocaleString()}</TableCell>
                    <TableCell>{log.action}</TableCell>
                    <TableCell>
                      {log.resourceType}
                      {log.resourceId ? ` · ${log.resourceId}` : ""}
                    </TableCell>
                    <TableCell>{log.userId ?? "—"}</TableCell>
                    <TableCell>{log.ipAddress ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
